import React, { useState } from 'react';
import { Box, Button, OutlineWarningIcon, PagePanel, Text } from 'edenred-ui';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { EmptyState } from '@epi-shared/components';

type ErrorBoundaryFallbackProps = {
  error: Error;
  errorInfo: React.ErrorInfo | null;
  onReload: () => void;
};

export function formatErrorDetails(
  error: Error,
  errorInfo: React.ErrorInfo | null
) {
  return [
    `Time: ${new Date().toISOString()}`,
    `URL: ${window.location.href}`,
    `Error: ${error.name}: ${error.message}`,
    errorInfo?.componentStack
      ? `Component stack:${errorInfo.componentStack}`
      : '',
    error.stack ? `Stack:\n${error.stack}` : ''
  ]
    .filter(Boolean)
    .join('\n\n');
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function ErrorBoundaryFallback({
  error,
  errorInfo,
  onReload
}: ErrorBoundaryFallbackProps) {
  const { t } = useTranslation();
  const { gray5, sizeSmall } = useTheme();
  const [copied, setCopied] = useState(false);
  const details = formatErrorDetails(error, errorInfo);

  const handleCopy = async () => {
    setCopied(await copyText(details));
  };

  return (
    <PagePanel>
      <Box
        mt={6}
        minHeight={600}
        display="flex"
        flexDirection="column"
        alignItems="center"
        id="error-boundary-fallback"
      >
        <EmptyState
          icon={<OutlineWarningIcon />}
          title={t('components.error_boundary.title')}
          description={t('components.error_boundary.message')}
          btnText={t('components.error_boundary.reload_button')}
          btnOnClick={onReload}
        >
          <Box mt={4} maxWidth={640} width="100%">
            <Text>{t('components.error_boundary.details_hint')}</Text>
            <Box
              id="error-boundary-details"
              bgcolor={gray5}
              mt={2}
              p={2}
              maxHeight={200}
              overflow="auto"
              fontSize={sizeSmall}
              whiteSpace="pre-wrap"
            >
              {details}
            </Box>
            <Box mt={2} display="flex" justifyContent="center">
              <Button id="error-boundary-copy-btn" onClick={handleCopy}>
                {copied
                  ? t('components.error_boundary.copied')
                  : t('components.error_boundary.copy_button')}
              </Button>
            </Box>
          </Box>
        </EmptyState>
      </Box>
    </PagePanel>
  );
}
